import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Checkbox,
  Pagination,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconBrandTabler,
  IconEye,
} from "@tabler/icons-react";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";

import DeleteModal from "./DeleteModal";
import classes from "./Prebuild.module.css";
import { RowDataBuildConfigEasy } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { buildConfigEasy } from "../types/RowDataTypes";
import { CustomTable } from "../Table/CustomTable";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import Header from "../static/Header";
import getBuildConfigEasyTable from "../../DataDisplay/DataDisplayTable/BuildConfigEasyTable";
import { Link, useNavigate } from "react-router-dom";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  NAME = "Title",
//   CATEGORY = "Category",
//   PROCESSOR = "Processor",
//   GRAPHIC = "Graphic",
//     PRICE = "Price",
  SHOW = "Show",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
    [colFilter.NAME]: "title",
    // [colFilter.CATEGORY]: "category",
    // [colFilter.PROCESSOR]: "processor",
    // [colFilter.GRAPHIC]: "graphic",
    // [colFilter.PRICE]: "price",
    [colFilter.SHOW]: "show",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilter.NAME.toLowerCase(),
    colFilter.SHOW.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text className={classes.Text}>{children}</Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataBuildConfigEasy[] = [
  {
    id: "",
    buildId: "",
    title: "",
    images: "",
    cpu: 0,
    cooler: 0,
    gpu: 0,
    mobo: 0,
    case: 0,
    ram: 0,
    psu: 0,
    ssd1: 0,
    ssd2: 0,
    isPrebuild: false,
    isCustom: false,
    isEasy: false,
    show: false,
    action: "",
  },
];

export function BuildConfigEasy() {
  const axiosPrivate = useAxiosPrivate();
 const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [tableData, setTableData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataBuildConfigEasy | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  //Used in searching and when deleting to cause rerender to fetch data
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.NAME],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const toggleVisibility = async (row: RowDataBuildConfigEasy, e: any) => {
    e.target.disabled = true;
    let endpoint;
    if (!row.show) endpoint = "/api/admin/build-config/show";
    else endpoint = "/api/admin/build-config/hide";

    await axiosPrivate
      .post(endpoint, {
        id: row.id,
      })
      .then((response) => {
        const indexOfSelectedRow = rowDataBanner!!.findIndex((e) => e === row);

        // Create a copy of the sortedData
        const updatedData = [...rowDataBanner!!];

        // Update the selectedRow in the copied data
        updatedData[indexOfSelectedRow] = {
          ...row,
          show: !row.show,
        };

        setRowDataBanner(updatedData);
      })
      .catch((err) => {
        showErrorAlert("An error occured");
      });

    e.target.disabled = false;
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    buildConfigEasy,
    getBuildConfigEasyTable().create(),
    "",
  );
  const itemsOnPage = useRef(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  // const totalRows = fetchedData.totalRows;
  let rowData: RowDataBuildConfigEasy[] | null = fetchedData.data as RowDataBuildConfigEasy[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataBanner, setRowDataBanner] = useState<RowDataBuildConfigEasy[] | null>(
    null
  );

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataBuildConfigEasy | null>(null);


  const handleDelete = (row: RowDataBuildConfigEasy) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/build-config/delete", {
        id: selectedRowToDelete!!.id,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });

    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const setSorting = (field: keyof RowDataBuildConfigEasy) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  useEffect(() => {
    setRowDataBanner(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const rowsToMap = rowDataBanner ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.title}</Table.Td>
      {/* <Table.Td className={classes.imageLink}>{row.img}</Table.Td> */}
      {/* <Table.Td className={classes.colorAndButtonColumn}>{row.category}</Table.Td>
      <Table.Td className={classes.colorAndButtonColumn}>{row.processor}</Table.Td>
      <Table.Td className={classes.colorAndButtonColumn}>{row.graphic}</Table.Td>
      <Table.Td className={classes.colorAndButtonColumn}>RM{row.price}</Table.Td> */}
      <Table.Td className={classes.colorAndButtonColumn}>
        <Button
          className={classes.showButton}
          style={{
            backgroundColor: row.show ? "#2BDD66" : "#0014AD",
          }}
          onClick={(e) => toggleVisibility(row, e)}
        >
          {row.show ? "ON" : "OFF"}
        </Button>
      </Table.Td>
      <Table.Td className={classes.actionButtons}>
        <UnstyledButton component={Link} to={`/build-config-easy/update/${row.buildId}`}>
        <button  className={classes.editbutton}>
          <IconEdit /> Edit
        </button>
        </UnstyledButton>

        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.bannerPageContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Easy Build Config" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            <Button component={Link} to={'/build-config-easy/create'} className={classes.addButton}>
              {/* <IconBrandTabler className={classes.iconBrandTabler} /> */}
              Create Easy
            </Button>
          </div>
        </div>

        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />

        <CustomTable
          colHeaders={Object.values(ColumnHeaderToFilterPair)}
          filters={Object.values(colFilters)}
          reverseSortDirection={reverseSortDirection}
          sortBy={sortBy}
          setSorting={setSorting}
          customclasses={classes}
          Th={Th}
          rowData={rowDataBanner}
          rows={rows}
        />

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default BuildConfigEasy;
